'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import styles from './UiKitModal.module.css';

export const UiKitModal = memo(
  ({
    children,
    onClose,
    show,
    canClose = false,
    contentClassName,
  }: {
    children: ReactNode;
    show: boolean;
    onClose?: () => void;
    canClose?: boolean;
    contentClassName?: string;
  }) => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);

      return () => setMounted(false);
    }, []);

    return (
      <div>
        {mounted &&
          createPortal(
            <AnimatePresence>
              {show && (
                <motion.div
                  className={styles.modalWrapper}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  <motion.div
                    onClick={onClose}
                    className={styles.backdrop}
                    initial={{ backdropFilter: 'blur(0px)' }}
                    animate={{ backdropFilter: 'blur(4px)' }}
                    exit={{ backdropFilter: 'blur(0px)' }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  />
                  <motion.div
                    className={classNames(styles.content, contentClassName)}
                    initial={{ opacity: 0, borderRadius: 0 }}
                    animate={{ opacity: 1, borderRadius: 24 }}
                    exit={{ opacity: 0, borderRadius: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    {canClose && <button onClick={onClose}>Close Modal</button>}
                    {children}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>,
            document.body,
          )}
      </div>
    );
  },
);

UiKitModal.displayName = 'UiKitModal';
